// File: /frontend/src/contexts/SignalRContext.tsx
import React, { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { SignalRContext } from './SignalRContextProvider';
import logger from '../utils/logger';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';

interface ISignalRContext {
  connection: signalR.HubConnection | null;
  isConnected: boolean; // Add isConnected state
}

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false); // Track connection state

  // Determine hub URL based on environment
  const host = window.location.hostname;
  const hubUrl = import.meta.env.PROD 
    ? `https://${host}/hub/grid` 
    : import.meta.env.VITE_SIGNALR_URL || 'http://localhost:5200/hub/grid';


  useEffect(() => {
    const  customRetryPolicy: signalR.IRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) => {
      if (retryContext.previousRetryCount < 8) {  // 8 retries should allow for trains and tunnels!
        return 1000 * Math.pow(2, retryContext.previousRetryCount); // Exponential backoff
      }
      return null; // Stop retrying after 8 attempts
    },
  };
  let newConnection: signalR.HubConnection;
  try {
    newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { withCredentials: true })
      .withHubProtocol(new MessagePackHubProtocol()) // Use MessagePack protocol
      .withAutomaticReconnect(customRetryPolicy)
      .withStatefulReconnect()
      // .configureLogging(signalR.LogLevel.Debug)
      .build();
  } catch (error) {
    logger.error('Error creating SignalR connection:', error);
    newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { withCredentials: true })
      .withAutomaticReconnect(customRetryPolicy)
      .withStatefulReconnect()
      .build();
  }

    newConnection.onreconnecting((err) => {
      logger.warn('SignalR reconnecting:', err);
      setIsConnected(false);
    });

    newConnection.onreconnected((connectionId) => {
      logger.info('SignalR reconnected, connectionId:', connectionId);
      setIsConnected(true);
    });

    newConnection.onclose(() => { // set isConnected to false on close
      logger.info('SignalR Disconnected:', hubUrl);
      setIsConnected(false);
    });

    newConnection
      .start()
      .then(() => {
        logger.info('SignalR Connected:', hubUrl);
        setIsConnected(true); // Set isConnected to true after successful connection
      })
      .catch((err: unknown) => logger.error('SignalR Connection Error: ', err));
    // Store connection object immediately after creation - needed for cleanup and context consumers
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConnection(newConnection);

    return () => {
      newConnection.stop();
    };
  }, [hubUrl]);

  const contextValue: ISignalRContext = {
    connection,
    isConnected, // Expose isConnected state
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

